<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Attendance Summary</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="gotTo('employee-dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item active">Summary</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading attendance summary...</p>
          </div>

          <div v-else-if="summaryData">
            <!-- Month Selector -->
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-body">
                    <label class="form-label">Select Month</label>
                    <input
                        v-model="selectedMonth"
                        type="month"
                        class="form-control"
                        @change="loadSummary"
                        :max="maxMonth">
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card bg-primary text-white">
                  <div class="card-body">
                    <h6 class="text-white-50 mb-2">Performance Score</h6>
                    <div class="d-flex align-items-center">
                      <h1 class="display-4 mb-0 me-3 text-white">{{ summaryData.overview.performanceScore }}%</h1>
                      <div class="flex-grow-1">
                        <div class="progress bg-white bg-opacity-25" style="height: 8px;">
                          <div
                              class="progress-bar bg-white"
                              :style="{width: summaryData.overview.performanceScore + '%'}">
                          </div>
                        </div>
                        <small class="text-white-50 mt-1 d-block">
                          {{ getPerformanceLabel(summaryData.overview.performanceScore) }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Statistics Cards -->
            <div class="row">
              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                          <i class="mdi mdi-calendar-check"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Days Present</p>
                        <h4 class="mb-0">{{ summaryData.overview.daysPresent }}<span class="text-muted font-size-14">/ {{ summaryData.period.workingDays }}</span></h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                          <i class="mdi mdi-clock-outline"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Total Hours</p>
                        <h4 class="mb-0">{{ summaryData.overview.totalHours }}h</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-3">
                          <i class="mdi mdi-clock-alert-outline"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Late Days</p>
                        <h4 class="mb-0">{{ summaryData.overview.lateDays }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                          <i class="mdi mdi-clock-fast"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Overtime</p>
                        <h4 class="mb-0">{{ summaryData.overview.totalOvertime }}h</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Attendance Rate & Location Breakdown -->
            <div class="row">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-chart-donut text-primary me-2"></i>
                      Attendance Rate
                    </h5>
                  </div>
                  <div class="card-body text-center">
                    <div class="mb-4">
                      <h1 class="display-3 text-primary mb-0">{{ summaryData.overview.attendanceRate }}%</h1>
                      <p class="text-muted">{{ summaryData.overview.daysPresent }} of {{ summaryData.period.workingDays }} working days</p>
                    </div>
                    <div class="row">
                      <div class="col-4">
                        <div class="p-3">
                          <p class="text-muted mb-1">On Time</p>
                          <h5 class="text-success mb-0">{{ summaryData.overview.onTimeDays }}</h5>
                        </div>
                      </div>
                      <div class="col-4 border-start border-end">
                        <div class="p-3">
                          <p class="text-muted mb-1">Late</p>
                          <h5 class="text-warning mb-0">{{ summaryData.overview.lateDays }}</h5>
                        </div>
                      </div>
                      <div class="col-4">
                        <div class="p-3">
                          <p class="text-muted mb-1">Absent</p>
                          <h5 class="text-danger mb-0">{{ summaryData.overview.absentDays }}</h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-map-marker text-primary me-2"></i>
                      Location Breakdown
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="summaryData.locationBreakdown && summaryData.locationBreakdown.length > 0">
                      <div v-for="(location, index) in summaryData.locationBreakdown" :key="index" class="mb-3">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <span :class="getLocationBadgeClass(location.location_type)">
                              {{ location.location_type }}
                            </span>
                            <small class="text-muted ms-2">{{ location.unique_days }} days</small>
                          </div>
                          <strong>{{ location.count }} entries</strong>
                        </div>
                        <div class="progress" style="height: 8px;">
                          <div
                              :class="getLocationProgressClass(location.location_type)"
                              :style="{width: getLocationPercentage(location.count) + '%'}">
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted py-4">
                      <i class="mdi mdi-map-marker-off font-size-48"></i>
                      <p class="mt-2">No location data available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Punctuality Analysis -->
            <div class="row">
              <div class="col-lg-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-timeline-clock text-primary me-2"></i>
                      Punctuality Analysis
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="row text-center">
                      <div class="col-md-3">
                        <div class="p-3">
                          <div class="avatar-sm mx-auto mb-2">
                            <span class="avatar-title bg-success rounded-circle">
                              <i class="mdi mdi-check font-size-20"></i>
                            </span>
                          </div>
                          <h4 class="mb-1">{{ summaryData.punctuality.onTimeCount }}</h4>
                          <p class="text-muted mb-0 small">On Time</p>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="p-3">
                          <div class="avatar-sm mx-auto mb-2">
                            <span class="avatar-title bg-warning rounded-circle">
                              <i class="mdi mdi-clock-alert font-size-20"></i>
                            </span>
                          </div>
                          <h4 class="mb-1">{{ summaryData.punctuality.late1to15 }}</h4>
                          <p class="text-muted mb-0 small">1-15 min late</p>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="p-3">
                          <div class="avatar-sm mx-auto mb-2">
                            <span class="avatar-title bg-orange rounded-circle">
                              <i class="mdi mdi-alarm font-size-20"></i>
                            </span>
                          </div>
                          <h4 class="mb-1">{{ summaryData.punctuality.late16to30 }}</h4>
                          <p class="text-muted mb-0 small">16-30 min late</p>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="p-3">
                          <div class="avatar-sm mx-auto mb-2">
                            <span class="avatar-title bg-danger rounded-circle">
                              <i class="mdi mdi-alert-circle font-size-20"></i>
                            </span>
                          </div>
                          <h4 class="mb-1">{{ summaryData.punctuality.lateOver30 }}</h4>
                          <p class="text-muted mb-0 small">Over 30 min late</p>
                        </div>
                      </div>
                    </div>
                    <div class="text-center mt-3 pt-3 border-top">
                      <p class="text-muted mb-0">
                        Average Late Time: <strong>{{ summaryData.punctuality.avgLateMinutes }} minutes</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Weekly Breakdown & Top Late Days -->
            <div class="row">
              <div class="col-lg-7">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-calendar-week text-primary me-2"></i>
                      Weekly Breakdown
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Week</th>
                          <th class="text-center">Days</th>
                          <th class="text-center">Hours</th>
                          <th class="text-center">Late</th>
                          <th class="text-center">Avg Hours/Day</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(week, index) in summaryData.weeklyBreakdown" :key="index">
                          <td>
                            <strong>Week {{ index + 1 }}</strong>
                            <br>
                            <small class="text-muted">{{ formatDate(week.week_start) }} - {{ formatDate(week.week_end) }}</small>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-primary">{{ week.days_worked }}</span>
                          </td>
                          <td class="text-center">
                            <strong>{{ parseFloat(week.total_hours).toFixed(1) }}h</strong>
                          </td>
                          <td class="text-center">
                            <span v-if="week.late_count > 0" class="badge bg-warning">{{ week.late_count }}</span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td class="text-center">
                            {{ (parseFloat(week.total_hours) / parseInt(week.days_worked)).toFixed(1) }}h
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-5">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-alert-circle text-warning me-2"></i>
                      Top Late Days
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="summaryData.topLateDays && summaryData.topLateDays.length > 0">
                      <div v-for="(day, index) in summaryData.topLateDays" :key="index" class="d-flex align-items-center mb-3 pb-3 border-bottom">
                        <div class="flex-shrink-0">
                          <div class="avatar-xs">
                            <span class="avatar-title rounded-circle bg-warning">
                              {{ index + 1 }}
                            </span>
                          </div>
                        </div>
                        <div class="flex-grow-1 ms-3">
                          <h6 class="mb-1">{{ formatDate(day.date) }}</h6>
                          <p class="text-muted mb-0 small">{{ day.day_name }} · Arrived: {{ day.first_in }}</p>
                        </div>
                        <div class="flex-shrink-0">
                          <span class="badge bg-warning">{{ day.late_minutes }} min</span>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted py-4">
                      <i class="mdi mdi-check-circle font-size-48 text-success"></i>
                      <p class="mt-2 mb-0">Great! No late arrivals</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Recent Activity -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-history text-primary me-2"></i>
                      Recent Activity
                    </h5>
                    <button @click="gotTo('employee-attendance-records')" class="btn btn-sm btn-outline-primary">
                      <i class="mdi mdi-arrow-right me-1"></i>
                      View All Records
                    </button>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm table-hover mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Date & Time</th>
                          <th>Action</th>
                          <th>Location Type</th>
                          <th>Location</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(activity, index) in summaryData.recentActivity" :key="index">
                          <td>
                            <strong>{{ formatDate(activity.date) }}</strong>
                            <br>
                            <small class="text-muted">{{ activity.time }}</small>
                          </td>
                          <td>
                              <span :class="activity.direction === 'enter' ? 'badge bg-success' : 'badge bg-danger'">
                                {{ activity.direction === 'enter' ? 'Clock In' : 'Clock Out' }}
                              </span>
                          </td>
                          <td>
                              <span v-if="activity.location_type" :class="getLocationBadgeClass(activity.location_type)">
                                {{ activity.location_type }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                            <div v-if="activity.location_name || activity.address">
                              <div v-if="activity.location_name">
                                <strong>{{ activity.location_name }}</strong>
                              </div>
                              <div v-if="activity.address" class="text-muted small">
                                <i class="mdi mdi-map-marker me-1"></i>
                                {{ activity.address }}
                              </div>
                            </div>
                            <span v-else class="text-muted">-</span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeAttendanceSummary",
  components: { NavigationBar },

  data() {
    return {
      loading: false,
      summaryData: null,
      selectedMonth: '',
      maxMonth: ''
    };
  },

  mounted() {
    // Set current month
    const now = new Date();
    this.selectedMonth = now.toISOString().slice(0, 7);
    this.maxMonth = this.selectedMonth;

    this.loadSummary();
  },

  methods: {
    async loadSummary() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeAttendanceSummary");
        data.append("month", this.selectedMonth);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.summaryData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load summary');
        }
      } catch (error) {
        this.$toast?.error("Failed to load attendance summary: " + error.message) ||
        console.error("Failed to load summary:", error);
      } finally {
        this.loading = false;
      }
    },

    getPerformanceLabel(score) {
      if (score >= 95) return 'Excellent';
      if (score >= 85) return 'Very Good';
      if (score >= 75) return 'Good';
      if (score >= 60) return 'Fair';
      return 'Needs Improvement';
    },

    getLocationBadgeClass(type) {
      const typeMap = {
        'Office': 'badge bg-success',
        'Remote': 'badge bg-info',
        'Outside': 'badge bg-warning'
      };
      return typeMap[type] || 'badge bg-secondary';
    },

    getLocationProgressClass(type) {
      const typeMap = {
        'Office': 'progress-bar bg-success',
        'Remote': 'progress-bar bg-info',
        'Outside': 'progress-bar bg-warning'
      };
      return typeMap[type] || 'progress-bar';
    },

    getLocationPercentage(count) {
      if (!this.summaryData || !this.summaryData.locationBreakdown) return 0;
      const total = this.summaryData.locationBreakdown.reduce((sum, loc) => sum + parseInt(loc.count), 0);
      return total > 0 ? (count / total) * 100 : 0;
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-xs {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 500;
}

.bg-orange {
  background-color: #fd7e14;
  color: white;
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .display-3 {
    font-size: 2.5rem;
  }

  .display-4 {
    font-size: 2rem;
  }
}
</style>